import { useState, useEffect, useCallback } from 'react';
import { clientApiService } from '../services/clientApi';
import { ApiResponse } from '../types/api';

interface UseClientApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseClientApiOptions {
  immediate?: boolean;
  dependencies?: any[];
}

// Hook pour les statistiques client
export function useClientDashboardStats(options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<any>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getClientDashboardStats();
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les souscriptions du client
export function useMySouscriptions(params: any = {}, options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<ApiResponse<any>>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  // Sérialiser les params pour éviter les boucles infinies
  const paramsString = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMySouscriptions(params);
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, [paramsString]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les échéanciers du client
export function useMyEcheanciers(params: any = {}, options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<ApiResponse<any>>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  // Sérialiser les params pour éviter les boucles infinies
  const paramsString = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMyEcheanciers(params);
      
      // Si l'API retourne directement un tableau (sans pagination), on l'encapsule
      if (Array.isArray(result)) {
        setState({ 
          data: { results: result, count: result.length }, 
          loading: false, 
          error: null 
        });
      } else {
        // Si l'API retourne un objet paginé, on l'utilise tel quel
        setState({ data: result, loading: false, error: null });
      }
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, [paramsString]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les paiements du client
export function useMyPaiements(params: any = {}, options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<ApiResponse<any>>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  // Sérialiser les params pour éviter les boucles infinies
  const paramsString = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMyPaiements(params);
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, [paramsString]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les notifications du client
export function useMyNotifications(params: any = {}, options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<ApiResponse<any>>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  // Sérialiser les params pour éviter les boucles infinies
  const paramsString = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMyNotifications(params);
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, [paramsString]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les mises à jour de projet du client
export function useMyProjectUpdates(options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<any[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMyProjectUpdates();
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour le profil du client
export function useMyProfile(options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<any>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getMyProfile();
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hook pour les projets disponibles
export function useAvailableProjects(options: UseClientApiOptions = {}) {
  const [state, setState] = useState<UseClientApiState<ApiResponse<any>>>({
    data: null,
    loading: false,
    error: null,
  });

  const { immediate = true, dependencies = [] } = options;

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await clientApiService.getAvailableProjects();
      setState({ data: result, loading: false, error: null });
    } catch (error: any) {
      setState({
        data: null,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Une erreur est survenue',
      });
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate, ...dependencies]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// Hooks pour les mutations client
export function useCreateSouscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await clientApiService.createSouscription(data);
      setLoading(false);
      return result;
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Une erreur est survenue');
      setLoading(false);
      throw error;
    }
  }, []);

  return { mutate, loading, error };
}

export function useCreatePaiement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await clientApiService.createPaiement(data);
      setLoading(false);
      return result;
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Une erreur est survenue');
      setLoading(false);
      throw error;
    }
  }, []);

  const createMultiSubscriptionPaiement = useCallback(async (data: {
    montant_total_paye: number;
    date_paiement: string;
    mode_paiement: string;
    reference_paiement?: string;
    commentaire?: string;
    repartitions: Array<{
      souscription_id: number;
      montant_alloue: number;
    }>;
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await clientApiService.createMultiSubscriptionPaiement(data);
      setLoading(false);
      return result;
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Une erreur est survenue');
      setLoading(false);
      throw error;
    }
  }, []);

  return { mutate, createMultiSubscriptionPaiement, loading, error };
}

export function useUpdateProfile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.id;
      
      if (!userId) {
        throw new Error('Utilisateur non connecté');
      }
      
      const result = await clientApiService.updateMyProfile(userId, data);
      setLoading(false);
      return result;
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Une erreur est survenue');
      setLoading(false);
      throw error;
    }
  }, []);

  return { mutate, loading, error };
}
